import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetProformaList,
  GetProformaDetail,
  CreateProforma,
  UpdateProforma,
  DeleteProforma,
} from "../../Services/SaleService";

/**
 * Proforma State Interface with caching and optimization
 */
export interface ProformaState {
  // List data
  proformaCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  
  // Detail data
  selectedProformaDetail: any | null;
  
  // Loading states
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;
  
  // Error states
  error: string | null;
  errorDetail: string | null;
  
  // Metadata
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  
  // Cache management
  cache: Map<string, CacheEntry>;
  lastFetchParams: FetchParams | null;
  
  // Success flags for UX
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  financialYear: string;
  saleType: string;
  startDate: string;
  endDate: string;
}

const CACHE_DURATION = 5 * 60 * 1000;

const generateCacheKey = (params: FetchParams): string => {
  return `proforma_list_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}_${params.saleType}`;
};

const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

export const fetchProformaList = createAsyncThunk(
  "proforma/fetchProformaList",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      pageIndex: number;
      pageSize: number;
      sortColumn: string;
      sortDirection: string;
      searchValue: string;
      financialYear: string;
      saleType: string;
      startDate: string;
      endDate: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const root = getState() as { proforma: ProformaState };
      const cacheKey = generateCacheKey(params);
      
      if (root.proforma.cache.has(cacheKey)) {
        const cached = root.proforma.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);
      urlParams.append("financialYear", params.financialYear);
      urlParams.append("saleType", params.saleType);
      urlParams.append("startDate", params.startDate);
      urlParams.append("endDate", params.endDate);

      const result: any = await GetProformaList(urlParams);
      
      if (result?.data?.data?.proformaCollection) {
        return result.data.data;
      }
      
      return rejectWithValue("No proforma data received");
    } catch (error: any) {
      console.error("Error fetching proforma list:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch proformas"
      );
    }
  }
);

export const fetchProformaDetail = createAsyncThunk(
  "proforma/fetchProformaDetail",
  async (
    params: {
      consumerID: number;
      firmId: number;
      branchId: number;
      proformaID: number;
      financialYear: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append("consumerID", params.consumerID.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("proformaID", params.proformaID.toString());
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetProformaDetail(urlParams);
      
      if (result?.data?.data) {
        return result.data.data;
      }
      
      return rejectWithValue("No proforma detail received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch proforma details"
      );
    }
  }
);

export const createProformaAsync = createAsyncThunk(
  "proforma/createProforma",
  async (proformaData: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateProforma(proformaData);
      if (result?.data) return result.data;
      return rejectWithValue("Failed to create proforma");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to create proforma"
      );
    }
  }
);

export const updateProformaAsync = createAsyncThunk(
  "proforma/updateProforma",
  async (proformaData: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateProforma(proformaData);
      if (result?.data) return result.data;
      return rejectWithValue("Failed to update proforma");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to update proforma"
      );
    }
  }
);

export const deleteProformaAsync = createAsyncThunk(
  "proforma/deleteProforma",
  async (deleteData: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteProforma(deleteData);
      if (result?.data) return deleteData;
      return rejectWithValue("Failed to delete proforma");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to delete proforma"
      );
    }
  }
);

const initialState: ProformaState = {
  proformaCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedProformaDetail: null,
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  cache: new Map(),
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const proformaSlice = createSlice({
  name: "proforma",
  initialState,
  reducers: {
    clearProformaData: (state) => {
      state.proformaCollection = [];
      state.totalCount = 0;
      state.selectedProformaDetail = null;
      state.error = null;
    },
    clearProformaDetail: (state) => {
      state.selectedProformaDetail = null;
      state.errorDetail = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
    },
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },
    invalidateCache: (state) => {
      state.cache.clear();
    },
    setFetchMetadata: (state, action: PayloadAction<Partial<FetchParams>>) => {
      state.searchValue = action.payload.searchValue || state.searchValue;
      state.sortColumn = action.payload.sortColumn || state.sortColumn;
      state.sortDirection = action.payload.sortDirection || state.sortDirection;
      state.pageIndex = action.payload.pageIndex || state.pageIndex;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProformaList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchProformaList.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingList = false;
        state.proformaCollection = action.payload?.proformaCollection || [];
        state.totalCount = action.payload?.totalCount || 0;
        state.pageIndex = action.payload?.pageIndex || 1;
        state.pageSize = action.payload?.pageSize || 10;
        if (state.lastFetchParams) {
          const cacheKey = generateCacheKey(state.lastFetchParams);
          state.cache.set(cacheKey, {
            data: action.payload,
            timestamp: Date.now(),
            expiresIn: CACHE_DURATION,
          });
        }
      })
      .addCase(fetchProformaList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchProformaDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(fetchProformaDetail.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingDetail = false;
        state.selectedProformaDetail = action.payload;
      })
      .addCase(fetchProformaDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    builder
      .addCase(createProformaAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createProformaAsync.fulfilled, (state) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear();
      })
      .addCase(createProformaAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateProformaAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateProformaAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(updateProformaAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteProformaAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteProformaAsync.fulfilled, (state) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear();
      })
      .addCase(deleteProformaAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearProformaData,
  clearProformaDetail,
  clearErrors,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = proformaSlice.actions;

export default proformaSlice.reducer;
